import React, { useReducer, useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { notificationEnter } from './ui/animations';
import { stackApp } from './stackApp';
import { initialGameState } from './game/initialState';
import { gameReducer } from './game/reducer';
import { music } from './game/systems/MusicSystem';
import { Header } from './components/ui/Header';
import { FacilityScreen } from './components/screens/FacilityScreen';
import { RoomDetailScreen } from './components/screens/RoomDetailScreen';
import { WorkforceScreen } from './components/screens/WorkforceScreen';
import { MarketScreen } from './components/screens/MarketScreen';
import { TechTreeScreen } from './components/screens/TechTreeScreen';
import { GeneticsScreen } from './components/screens/GeneticsScreen';
import { LabDetailScreen } from './components/screens/LabDetailScreen';
import { LeaderboardScreen } from './components/screens/LeaderboardScreen';
import { MarketingScreen } from './components/screens/MarketingScreen';
import { WelcomeModal } from './components/ui/WelcomeModal';
import { TutorialOverlay } from './components/ui/TutorialOverlay';
import { NextActionBar } from './components/ui/NextActionBar';
import { AuthModal } from './components/ui/AuthModal';
import { CashFlyUp } from './components/ui/CashFlyUp';
import { HarvestCelebration } from './components/ui/HarvestCelebration';
import { PhenoHuntModal } from './components/ui/PhenoHuntModal';
import { CorporateOfferModal } from './components/ui/CorporateOfferModal';
import { useCloudSync } from './hooks/useCloudSync';

const SAVE_KEY = 'canntycoon_save_v1';
const AUTO_SAVE_INTERVAL = 60; // ticks

// ── Save / Load helpers ───────────────────────────────────────────────────────
function saveGame(state) {
  try {
    const { ui: _ui, ...toSave } = state;
    localStorage.setItem(SAVE_KEY, JSON.stringify({ ...toSave, ui: { ...state.ui, lastSaved: Date.now() } }));
  } catch (e) {
    console.warn('Save failed:', e);
  }
}

function loadGame() {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    // Migrate: remove corrupted inventory items (grams=NaN/0 from old FULFILL_CONTRACT bug)
    if (Array.isArray(saved.inventory)) {
      const seen = new Set();
      saved.inventory = saved.inventory.filter(item => {
        if (!item || item.status === 'sold' || !Number.isFinite(item.grams) || item.grams <= 0) return false;
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
    }
    // Migrate: ensure active contracts have gramsDelivered (missing in old saves → NaN arithmetic)
    if (Array.isArray(saved.activeContracts)) {
      saved.activeContracts = saved.activeContracts.map(c => ({
        ...c,
        gramsDelivered: c.gramsDelivered ?? 0,
      }));
    }
    return saved;
  } catch {
    return null;
  }
}

// ── Screen router ─────────────────────────────────────────────────────────────
function renderScreen(screen, state, dispatch) {
  const props = { state, dispatch };
  switch (screen) {
    case 'facility':   return <FacilityScreen {...props} />;
    case 'room':       return <RoomDetailScreen {...props} />;
    case 'workforce':  return <WorkforceScreen  {...props} />;
    case 'market':     return <MarketScreen     {...props} />;
    case 'research':     return <TechTreeScreen    {...props} />;
    case 'genetics':     return <GeneticsScreen    {...props} />;
    case 'leaderboard':  return <LeaderboardScreen {...props} />;
    case 'lab':          return <LabDetailScreen   {...props} />;
    case 'marketing':    return <MarketingScreen  {...props} />;
    default:             return <FacilityScreen {...props} />;
  }
}

// ── Notification toast (bottom-left, auto-dismiss) ────────────────────────────
const CRITICAL_KEYWORDS = ['outbreak', 'harvest ready', 'inspection', 'shut down', 'investigation'];
function isCriticalNotif(n) {
  if (n.type === 'danger') return true;
  const msg = (n.message || '').toLowerCase();
  return CRITICAL_KEYWORDS.some(kw => msg.includes(kw));
}

function ToastItem({ n, dispatch }) {
  useEffect(() => {
    if (isCriticalNotif(n)) return; // critical toasts require manual close
    const timer = setTimeout(() => {
      dispatch({ type: 'DISMISS_NOTIFICATION', payload: { id: n.id } });
    }, 4000);
    return () => clearTimeout(timer);
  }, [n.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.div
      layout
      className={`notif-item ${n.type}`}
      initial={notificationEnter.initial}
      animate={notificationEnter.animate}
      exit={notificationEnter.exit}
      transition={notificationEnter.transition}
    >
      <span>{n.message}</span>
      <button className="notif-dismiss" onClick={() => dispatch({ type: 'DISMISS_NOTIFICATION', payload: { id: n.id } })}>✕</button>
    </motion.div>
  );
}

function NotificationToast({ notifications, dispatch }) {
  const recent = notifications.filter(n => !n.read).slice(-3);
  return (
    <div className="notif-panel" style={{ left: 12, right: 'auto', bottom: 12 }}>
      <AnimatePresence mode="popLayout">
        {recent.map(n => (
          <ToastItem key={n.id} n={n} dispatch={dispatch} />
        ))}
      </AnimatePresence>
    </div>
  );
}

// ── Main Game Component ───────────────────────────────────────────────────────
function useOptionalUser() {
  const { useUser } = stackApp ? require('@stackframe/react') : { useUser: () => null };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return stackApp ? useUser({ or: 'return-null' }) : null;
}

export function Game() {
  const user = useOptionalUser();
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const savedState = loadGame();
  const [state, dispatch] = useReducer(
    gameReducer,
    savedState ? { ...initialGameState, ...savedState, paused: true } : initialGameState
  );

  const { cloudStatus, doSave, skipUnloadSave } = useCloudSync(state, dispatch, user);

  // Expose dispatch for Playwright e2e tests
  useEffect(() => {
    window.__gameDispatch = dispatch;
    return () => { delete window.__gameDispatch; };
  }, [dispatch]);

  const tickRef = useRef(state.gameSpeed);
  tickRef.current = state.gameSpeed;
  const pausedRef = useRef(state.paused);
  pausedRef.current = state.paused || state.phenoCandidates !== null;

  // ── Game loop ────────────────────────────────────────────────────────────
  useEffect(() => {
    let loopId;
    function tick() {
      if (!pausedRef.current) {
        dispatch({ type: 'TICK' });
      }
      loopId = setTimeout(tick, tickRef.current);
    }
    loopId = setTimeout(tick, tickRef.current);
    return () => clearTimeout(loopId);
  }, []); // runs once on mount

  // ── Auto-save every AUTO_SAVE_INTERVAL ticks ─────────────────────────────
  useEffect(() => {
    if (state.tick > 0 && state.tick % AUTO_SAVE_INTERVAL === 0) {
      saveGame(state);
    }
  }, [state.tick]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keyboard shortcuts ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === ' ' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        dispatch({ type: 'TOGGLE_PAUSE' });
      }
      if (e.key === '1') dispatch({ type: 'SET_GAME_SPEED', payload: { speed: 1000 } });
      if (e.key === '2') dispatch({ type: 'SET_GAME_SPEED', payload: { speed: 400 } });
      if (e.key === '3') dispatch({ type: 'SET_GAME_SPEED', payload: { speed: 150 } });
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const activeScreen = state.ui?.activeScreen || 'facility';

  // ── Music: switch track based on active screen ───────────────────────────
  const [musicMuted, setMusicMuted] = useState(false);

  useEffect(() => {
    if (musicMuted) return;
    const menuScreens = ['research', 'genetics', 'leaderboard', 'lab'];
    const track = menuScreens.includes(activeScreen) ? 'menu' : 'gameplay';
    music.play(track);
  }, [activeScreen, musicMuted]);

  // ── Music: achievement sting on harvest / unlock notifications ───────────
  const prevNotifCount = useRef(state.notifications.length);
  useEffect(() => {
    const curr = state.notifications;
    if (curr.length > prevNotifCount.current) {
      const newest = curr[curr.length - 1];
      const isAchieve = newest && (
        newest.type === 'success' &&
        /(harvest|unlock|unlocked|research complete|stage|contract fulfilled)/i.test(newest.message || '')
      );
      if (isAchieve && !musicMuted) music.achieve();
    }
    prevNotifCount.current = curr.length;
  }, [state.notifications, musicMuted]);

  const toggleMute = useCallback(() => {
    const next = !musicMuted;
    setMusicMuted(next);
    music.setMuted(next);
    if (!next) {
      const menuScreens = ['research', 'genetics', 'leaderboard', 'lab'];
      const track = menuScreens.includes(activeScreen) ? 'menu' : 'gameplay';
      music.play(track);
    } else {
      music.stop();
    }
  }, [musicMuted, activeScreen]);

  // Show WelcomeModal for step 0 of tutorial
  const showWelcome = state.tutorial?.enabled && state.tutorial?.currentStep === 0 && !state.tutorial?.welcomeDismissed;
  // Show TutorialOverlay for steps 1+
  const showTutorial = state.tutorial?.enabled && state.tutorial?.welcomeDismissed && state.tutorial?.currentStep > 0;

  // ── Game Over screen ─────────────────────────────────────────────────────
  if (state.isShutDown) {
    return (
      <div className="game-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="panel" style={{ maxWidth: 480, width: '100%', textAlign: 'center', padding: '40px 32px' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🚨</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--red)', marginBottom: 8 }}>Operation Shut Down</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24, lineHeight: 1.6 }}>
            {state.shutdownReason || 'Your facility was shut down following a regulatory inspection.'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 28 }}>
            <div style={{ background: 'var(--border-light)', borderRadius: 'var(--radius-sm)', padding: '12px 8px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Days Survived</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{state.gameDay}</div>
            </div>
            <div style={{ background: 'var(--border-light)', borderRadius: 'var(--radius-sm)', padding: '12px 8px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Total Revenue</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--green)' }}>${state.totalRevenue.toLocaleString()}</div>
            </div>
            <div style={{ background: 'var(--border-light)', borderRadius: 'var(--radius-sm)', padding: '12px 8px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Reputation</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--yellow)' }}>{Math.round(state.reputation)}</div>
            </div>
          </div>

          <button
            className="btn btn-primary"
            style={{ width: '100%', padding: '12px' }}
            onClick={() => {
              localStorage.removeItem(SAVE_KEY);
              window.location.reload();
            }}
          >
            🌱 Start New Game
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="game-root">
      <Header state={state} dispatch={dispatch} user={user} onOpenAuth={() => setAuthModalOpen(true)} cloudStatus={cloudStatus} doSave={doSave} skipUnloadSave={skipUnloadSave} musicMuted={musicMuted} onToggleMute={toggleMute} />
      <div className="game-body">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeScreen}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            {renderScreen(activeScreen, state, dispatch)}
          </motion.div>
        </AnimatePresence>
      </div>
      <NotificationToast notifications={state.notifications} dispatch={dispatch} />
      <CashFlyUp notifications={state.notifications} />
      <HarvestCelebration notifications={state.notifications} />
      {state.phenoCandidates && (
        <PhenoHuntModal phenoCandidates={state.phenoCandidates} dispatch={dispatch} />
      )}
      <NextActionBar state={state} dispatch={dispatch} />
      {showWelcome && <WelcomeModal dispatch={dispatch} user={user} />}
      {showTutorial && <TutorialOverlay tutorial={state.tutorial} dispatch={dispatch} />}
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
      {state.corporateOfferAvailable && <CorporateOfferModal dispatch={dispatch} />}
    </div>
  );
}
